import { useEffect, useRef } from 'react'

interface TurnstileApi {
  render(el: HTMLElement, opts: Record<string, unknown>): string
  remove(id: string): void
  reset(id: string): void
}
declare global {
  interface Window {
    turnstile?: TurnstileApi
  }
}

const SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
let loading: Promise<TurnstileApi> | null = null

function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile)
  loading ??= new Promise((resolve, reject) => {
    const s = document.createElement('script')
    s.src = SRC
    s.async = true
    s.onload = () => (window.turnstile ? resolve(window.turnstile) : reject(new Error('turnstile')))
    s.onerror = () => {
      loading = null
      reject(new Error('turnstile'))
    }
    document.head.appendChild(s)
  })
  return loading
}

/**
 * Cloudflare Turnstile (plan, section 11: sign-in and partner applications). Renders nothing when the
 * server has no site key; the token goes to the form and is single-use, so `resetKey` re-issues it.
 */
export function Turnstile({
  siteKey,
  onToken,
  resetKey = 0,
}: {
  siteKey: string | null | undefined
  onToken: (token: string | null) => void
  resetKey?: number
}) {
  const box = useRef<HTMLDivElement>(null)
  const widget = useRef<string | null>(null)
  const cb = useRef(onToken)
  cb.current = onToken

  useEffect(() => {
    if (!siteKey || !box.current) return
    let cancelled = false
    loadTurnstile()
      .then((ts) => {
        if (cancelled || !box.current) return
        widget.current = ts.render(box.current, {
          sitekey: siteKey,
          theme: 'dark',
          size: 'flexible',
          callback: (t: string) => cb.current(t),
          'expired-callback': () => cb.current(null),
          'error-callback': () => cb.current(null),
        })
      })
      .catch(() => cb.current(null))
    return () => {
      cancelled = true
      if (widget.current) window.turnstile?.remove(widget.current)
      widget.current = null
    }
  }, [siteKey])

  useEffect(() => {
    if (resetKey && widget.current) {
      cb.current(null)
      window.turnstile?.reset(widget.current)
    }
  }, [resetKey])

  if (!siteKey) return null
  return <div ref={box} className="mt-6 min-h-[65px]" />
}
